import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  LinearProgress,
  Tabs,
  Tab,
  Chip,
  Fade,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import { Iconify } from "../../../../components";
import { formatCurrency, handleApiErrors } from "../../../../utils/common";
import { merchantApi } from "../../../../redux/api/apiConfig";
import { MASTER_ACCOUNTS } from "../../../../ApiUrl";
import { toast } from "react-toastify";

const DepositAccount = ({ client }) => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentFundingIndex, setCurrentFundingIndex] = useState(0);
  const theme = useTheme();

  async function getDepositAccounts() {
    if (!client?.id) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await merchantApi.get(`${MASTER_ACCOUNTS}${client.id}/`);
      // Handle new API response structure
      const accountsData =
        response?.data?.data?.accounts || response?.data?.accounts || [];
      setAccounts(accountsData);
      if (accountsData.length > 0) {
        setSelectedTab(0);
        setCurrentFundingIndex(0);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // Handle errors
      const errorMessages = handleApiErrors(error);
      // Display each error in a separate snackbar
      errorMessages.forEach(({ field, message }) => {
        const formattedMessage = field ? `${field}: ${message}` : message;
        toast.error(formattedMessage);
      });
      // Fallback error message if no specific errors
      if (!errorMessages || errorMessages.length === 0) {
        toast.error(
          error?.response?.data?.message ||
            error?.data?.message ||
            "Error getting deposit account details"
        );
      }
    }
  }

  useEffect(() => {
    if (client?.id) {
      getDepositAccounts();
    }
  }, [client?.id]);

  useEffect(() => {
    // Reset funding account index when account tab changes
    setCurrentFundingIndex(0);
  }, [selectedTab]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handlePreviousFunding = () => {
    setCurrentFundingIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextFunding = () => {
    const selectedAccount = accounts[selectedTab];
    const fundingAccounts = selectedAccount?.funding_accounts || [];
    setCurrentFundingIndex((prev) =>
      Math.min(fundingAccounts.length - 1, prev + 1)
    );
  };

  const selectedAccount = accounts[selectedTab] || null;
  const fundingAccounts = selectedAccount?.funding_accounts || [];
  const currentFundingAccount =
    fundingAccounts[currentFundingIndex] || null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
          Deposit Account Details
        </Typography>

        {isLoading ? (
          <LinearProgress
            sx={{
              width: 1,
              maxWidth: 360,
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.common.accentColor,
              },
            }}
          />
        ) : accounts.length === 0 ? (
          <Card
            sx={{
              width: 1,
              boxShadow: "rgba(99, 99, 99, 0.2) 0px 0px 8px 0px",
              borderRadius: "8px",
              color: theme.palette.warning.darker,
              bgcolor: theme.palette.warning.lighter,
              p: 3,
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              No deposit accounts found
            </Typography>
          </Card>
        ) : (
          <>
            {/* Account Tabs Section */}
            <Card
              sx={{
                boxShadow: "rgba(99, 99, 99, 0.2) 0px 0px 8px 0px",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  bgcolor: theme.palette.common.lightAccentColor || "#F4F6F8",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    backgroundColor:
                      theme.palette.common.accentColor || "#1a1f64",
                  },
                  "& .MuiTab-root": {
                    minHeight: 72,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    alignItems: "flex-start",
                    textAlign: "left",
                    "&.Mui-selected": {
                      color: theme.palette.common.accentColor || "#1a1f64",
                    },
                  },
                }}
              >
                {accounts.map((account, index) => (
                  <Tab
                    key={`account-${index}-${account.currencyCode}`}
                    icon={
                      <TabContent
                        account={account}
                        isSelected={selectedTab === index}
                      />
                    }
                    iconPosition="start"
                    sx={{
                      alignItems: "flex-start",
                      py: 2,
                      textAlign: "left",
                      "& .MuiTab-iconWrapper": {
                        width: "100%",
                        textAlign: "left",
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Card>

            {/* Account Details Card with Funding Accounts Slider */}
            {selectedAccount && (
              <Fade in={true} timeout={300}>
                <Card
                  sx={{
                    boxShadow: "rgba(99, 99, 99, 0.2) 0px 0px 8px 0px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${alpha(
                      theme.palette.common.accentColor || "#1a1f64",
                      0.08
                    )}`,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 200,
                      height: 200,
                      opacity: 0.05,
                      background: `linear-gradient(135deg, ${
                        theme.palette.common.accentColor || "#1a1f64"
                      } 0%, ${theme.palette.secondary.main || "#1a1f64"} 100%)`,
                      borderRadius: "0 0 0 100%",
                    }}
                  />
                  <Stack spacing={3} sx={{ p: 3, position: "relative" }}>
                    {/* Account Header */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "8px",
                            bgcolor: alpha(
                              theme.palette.common.accentColor || "#1a1f64",
                              0.08
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Iconify
                            icon="mdi:bank-outline"
                            sx={{
                              width: 32,
                              height: 32,
                              color:
                                theme.palette.common.accentColor || "#1a1f64",
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {`${selectedAccount.currencyCode || "USD"} Account`}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", mt: 0.5 }}
                          >
                            {`${fundingAccounts.length} Funding Account${
                              fundingAccounts.length !== 1 ? "s" : ""
                            }`}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    {/* Account Level Details */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "repeat(1, 1fr)",
                          sm: "repeat(2, 1fr)",
                          md: "repeat(3, 1fr)",
                        },
                        gap: 3,
                        pt: 2,
                        pb: 2,
                        borderBottom: `1px solid ${alpha(
                          theme.palette.divider,
                          0.5
                        )}`,
                      }}
                    >
                      <AccountDetailItem
                        title="Currency"
                        content={selectedAccount.currencyCode || "--"}
                      />
                      <AccountDetailItem
                        title="Balance"
                        content={
                          selectedAccount.balance !== undefined
                            ? formatCurrency(
                                selectedAccount.balance,
                                2,
                                selectedAccount.currencyCode || "USD",
                                false
                              )
                            : "--"
                        }
                        highlight
                      />
                      <AccountDetailItem
                        title="Pending Amount"
                        content={
                          selectedAccount.pendingAmount !== undefined
                            ? formatCurrency(
                                selectedAccount.pendingAmount,
                                2,
                                selectedAccount.currencyCode || "USD",
                                false
                              )
                            : "--"
                        }
                        highlight
                      />
                    </Box>

                    {/* Funding Accounts Slider Section */}
                    {fundingAccounts.length > 0 ? (
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 3 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                          >
                            Funding Accounts
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              {currentFundingIndex + 1} of {fundingAccounts.length}
                            </Typography>
                            <IconButton
                              onClick={handlePreviousFunding}
                              disabled={currentFundingIndex === 0}
                              sx={{
                                width: 36,
                                height: 36,
                                border: `1px solid ${alpha(
                                  theme.palette.common.accentColor || "#1a1f64",
                                  0.2
                                )}`,
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.common.accentColor ||
                                      "#1a1f64",
                                    0.08
                                  ),
                                },
                                "&:disabled": {
                                  opacity: 0.3,
                                },
                              }}
                            >
                              <Iconify
                                icon="eva:arrow-ios-back-fill"
                                sx={{
                                  color:
                                    theme.palette.common.accentColor || "#1a1f64",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              onClick={handleNextFunding}
                              disabled={
                                currentFundingIndex === fundingAccounts.length - 1
                              }
                              sx={{
                                width: 36,
                                height: 36,
                                border: `1px solid ${alpha(
                                  theme.palette.common.accentColor || "#1a1f64",
                                  0.2
                                )}`,
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.common.accentColor ||
                                      "#1a1f64",
                                    0.08
                                  ),
                                },
                                "&:disabled": {
                                  opacity: 0.3,
                                },
                              }}
                            >
                              <Iconify
                                icon="eva:arrow-ios-forward-fill"
                                sx={{
                                  color:
                                    theme.palette.common.accentColor || "#1a1f64",
                                }}
                              />
                            </IconButton>
                          </Stack>
                        </Stack>

                        {/* Funding Account Card */}
                        <Fade
                          in={true}
                          timeout={300}
                          key={currentFundingIndex}
                        >
                          <Card
                            sx={{
                              boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 0px",
                              borderRadius: "8px",
                              border: `1px solid ${alpha(
                                theme.palette.common.accentColor || "#1a1f64",
                                0.12
                              )}`,
                              bgcolor: alpha(
                                theme.palette.common.accentColor || "#1a1f64",
                                0.02
                              ),
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: 150,
                                height: 150,
                                opacity: 0.03,
                                background: `linear-gradient(135deg, ${
                                  theme.palette.common.accentColor || "#1a1f64"
                                } 0%, ${
                                  theme.palette.secondary.main || "#1a1f64"
                                } 100%)`,
                                borderRadius: "0 0 0 100%",
                              }}
                            />
                            <Stack spacing={3} sx={{ p: 3, position: "relative" }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={2}
                              >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: "8px",
                                      bgcolor: alpha(
                                        theme.palette.common.accentColor ||
                                          "#1a1f64",
                                        0.1
                                      ),
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Iconify
                                      icon="mdi:account-details-outline"
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        color:
                                          theme.palette.common.accentColor ||
                                          "#1a1f64",
                                      }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {currentFundingAccount.accountName || "--"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "text.secondary", mt: 0.5 }}
                                    >
                                      {currentFundingAccount.accountNumber || "--"}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Stack>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "repeat(1, 1fr)",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                  },
                                  gap: 3,
                                }}
                              >
                                <AccountDetailItem
                                  title="Account ID"
                                  content={currentFundingAccount.accountId || "--"}
                                />
                                <AccountDetailItem
                                  title="Account Number"
                                  content={
                                    currentFundingAccount.accountNumber || "--"
                                  }
                                  highlight
                                />
                                <AccountDetailItem
                                  title="Account Name"
                                  content={
                                    currentFundingAccount.accountName || "--"
                                  }
                                />
                                <AccountDetailItem
                                  title="Currency"
                                  content={
                                    currentFundingAccount.currencyCode || "--"
                                  }
                                />
                                <AccountDetailItem
                                  title="SWIFT/BIC"
                                  content={currentFundingAccount.swiftBic || "--"}
                                />
                                <AccountDetailItem
                                  title="Country Code"
                                  content={
                                    currentFundingAccount.countryCode || "--"
                                  }
                                />
                                <AccountDetailItem
                                  title="Bank Name"
                                  content={currentFundingAccount.bank_name || "--"}
                                  highlight
                                />
                              </Box>
                            </Stack>
                          </Card>
                        </Fade>

                        {/* Slider Indicators */}
                        {fundingAccounts.length > 1 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            sx={{ mt: 2 }}
                          >
                            {fundingAccounts.map((_, index) => (
                              <Box
                                key={index}
                                onClick={() => setCurrentFundingIndex(index)}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor:
                                    currentFundingIndex === index
                                      ? theme.palette.common.accentColor ||
                                        "#1a1f64"
                                      : alpha(theme.palette.grey[500], 0.3),
                                  cursor: "pointer",
                                  transition: "all 0.2s ease-in-out",
                                  "&:hover": {
                                    bgcolor:
                                      currentFundingIndex === index
                                        ? theme.palette.common.accentColor ||
                                          "#1a1f64"
                                        : alpha(theme.palette.grey[500], 0.5),
                                    transform: "scale(1.2)",
                                  },
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ) : (
                      <Card
                        sx={{
                          width: 1,
                          boxShadow: "rgba(99, 99, 99, 0.1) 0px 2px 8px 0px",
                          borderRadius: "8px",
                          color: theme.palette.warning.darker,
                          bgcolor: theme.palette.warning.lighter,
                          p: 3,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body1">
                          No funding accounts found for this account
                        </Typography>
                      </Card>
                    )}
                  </Stack>
                </Card>
              </Fade>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

// Tab Content Component - Shows summary in tab
function TabContent({ account, isSelected }) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        width: "100%",
        py: 1,
        transition: "all 0.2s ease-in-out",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "8px",
          bgcolor: isSelected
            ? alpha(theme.palette.common.accentColor || "#1a1f64", 0.12)
            : alpha(theme.palette.grey[500], 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <Iconify
          icon="mdi:bank-outline"
          sx={{
            width: 24,
            height: 24,
            color: isSelected
              ? theme.palette.common.accentColor || "#1a1f64"
              : theme.palette.text.secondary,
          }}
        />
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: isSelected ? 700 : 600,
            color: isSelected
              ? theme.palette.common.accentColor || "#1a1f64"
              : theme.palette.text.primary,
            transition: "all 0.2s ease-in-out",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {`${account.currencyCode || "USD"} Account`}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {`${(account.funding_accounts || []).length} Funding Account${
            (account.funding_accounts || []).length !== 1 ? "s" : ""
          }`}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: isSelected
              ? theme.palette.common.accentColor || "#1a1f64"
              : theme.palette.text.primary,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {account.balance !== undefined
            ? `${account.currencyCode || "USD"} ${formatCurrency(
                account.balance,
                2,
                "",
                false
              )}`
            : `${account.currencyCode || "USD"} 0`}
        </Typography>
      </Stack>
    </Stack>
  );
}

// Account Detail Item Component
function AccountDetailItem({ title, content, highlight = false }) {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        cursor: "default",
        p: 2,
        borderRadius: "8px",
        bgcolor: highlight
          ? alpha(theme.palette.common.accentColor || "#1a1f64", 0.04)
          : "transparent",
        border: highlight
          ? `1px solid ${alpha(
              theme.palette.common.accentColor || "#1a1f64",
              0.12
            )}`
          : "none",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: highlight
            ? alpha(theme.palette.common.accentColor || "#1a1f64", 0.06)
            : alpha(theme.palette.grey[500], 0.04),
        },
      }}
      direction="column"
      spacing={0.5}
      alignItems="flex-start"
    >
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          fontSize: "0.7rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: highlight ? 600 : 500,
          color: highlight
            ? theme.palette.common.accentColor || "#1a1f64"
            : theme.palette.text.primary,
        }}
      >
        {content || "--"}
      </Typography>
    </Stack>
  );
}

export default DepositAccount;
